import axios from 'axios'
import * as Sentry from '@sentry/react'
import { signInWithPopup, signOut } from 'firebase/auth'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '../api/client'
import { CHATIZI_API_BASE_URL } from '../api/authRefresh'
import { AUTH_GOOGLE_VERIFY_PATH } from '../lib/authGooglePath'
import { formatApiError } from '../lib/apiErrorMessage'
import { growthbook, setTenantSlugAttribute } from '../lib/growthbookClient'
import i18n from '../i18n'
import {
  auth,
  getFirebaseAuthConfigHint,
  getGoogleProvider,
  isFirebaseAuthConfigured,
} from '../lib/firebase'
import {
  clearAuthToken,
  clearRefreshToken,
  getAuthToken,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
} from '../lib/authTokenStorage'
import { getMyBranchRole, getMyBranches } from '../api/branches'
import useManagerStore from './managerStore'
import useNotificationStore from './notificationStore'
import { normalizeRole } from '../lib/rbac'
import { clearPushOnLogout } from '../lib/pushNotifications'
import { queryClient } from '../lib/queryClient'

let fetchProfileInFlight = null
let bootstrapBranchesInFlight = null

// Query param mirroring the active branch (?b=<branchId>), so a refresh or a
// shared link lands on the right branch without waiting for the persisted
// store to rehydrate. localStorage (via managerStore) remains the fallback
// when the param is absent or stale.
const BRANCH_URL_PARAM = 'b'

function getBranchIdFromUrl() {
  return new URLSearchParams(window.location.search).get(BRANCH_URL_PARAM)
}

function syncBranchUrlParam(branchId) {
  const url = new URL(window.location.href)
  if (branchId) {
    if (url.searchParams.get(BRANCH_URL_PARAM) === branchId) return
    url.searchParams.set(BRANCH_URL_PARAM, branchId)
  } else {
    if (!url.searchParams.has(BRANCH_URL_PARAM)) return
    url.searchParams.delete(BRANCH_URL_PARAM)
  }
  window.history.replaceState(window.history.state, '', url)
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      orgId: null,
      branches: [],
      branchBootstrapStatus: 'idle',
      branchError: null,
      _hasHydrated: false,
      setHasHydrated: (val) => set({ _hasHydrated: val }),
      setToken: (token) => {
        setAuthToken(token)
        set({ token })
      },
      setUser: (user) => set({ user }),
      selectBranch: async (branchId) => {
        const branches = get().branches
        const targetBranch = branches.find((branch) => branch?.id === branchId)
        if (!targetBranch) return { ok: false, reason: 'branch_not_found' }

        const selectedTenantId = targetBranch?.tenant_id ?? null
        if (!selectedTenantId) {
          useManagerStore.getState().clearActiveBranchContext()
          syncBranchUrlParam(null)
          set({
            branchBootstrapStatus: 'error',
            branchError: i18n.t('branchGate.missingTenantId'),
          })
          return { ok: false, reason: 'missing_tenant_id' }
        }

        useManagerStore.getState().setActiveBranchContext({
          tenantId: selectedTenantId,
          tenantName: targetBranch?.name ?? null,
          branchId: targetBranch?.id ?? null,
        })
        setTenantSlugAttribute(targetBranch?.tenant_slug ?? null)
        syncBranchUrlParam(targetBranch?.id ?? null)
        const roleResult = await get().syncRoleForBranch(targetBranch?.id ?? null)
        set({
          branchBootstrapStatus: 'ready',
          branchError: roleResult.ok ? null : roleResult.error ?? null,
        })
        return { ok: true, roleSynced: roleResult.ok }
      },
      bootstrapBranches: async (orgIdArg) => {
        const orgId = orgIdArg ?? get().orgId ?? null
        if (
          bootstrapBranchesInFlight &&
          bootstrapBranchesInFlight.orgId === orgId
        ) {
          return bootstrapBranchesInFlight.promise
        }

        const requestPromise = (async () => {
          set({
            orgId,
            branchBootstrapStatus: 'loading',
            branchError: null,
          })

          try {
            const branches = await getMyBranches()
            if (!branches.length) {
              useManagerStore.getState().clearActiveBranchContext()
              syncBranchUrlParam(null)
              set({
                orgId,
                branches: [],
                branchBootstrapStatus: 'no_branch',
                branchError: null,
              })
              return { ok: false, reason: 'no_branch', branches: [] }
            }

            // Resolve which branch should be active: the URL param wins (so a
            // refresh or a shared link is exact), then the branch persisted
            // from the last session (survives a plain refresh with no param),
            // then the first branch if neither match (e.g. access revoked).
            const urlBranchId = getBranchIdFromUrl()
            const lastBranchId = useManagerStore.getState().branchId
            const branchFromUrl = urlBranchId
              ? branches.find((branch) => branch?.id === urlBranchId)
              : null
            const branchFromStore = lastBranchId
              ? branches.find((branch) => branch?.id === lastBranchId)
              : null
            const selectedBranch = branchFromUrl ?? branchFromStore ?? branches[0]
            const selectedTenantId = selectedBranch?.tenant_id ?? null
            if (!selectedTenantId) {
              useManagerStore.getState().clearActiveBranchContext()
              syncBranchUrlParam(null)
              set({
                orgId,
                branches,
                branchBootstrapStatus: 'error',
                branchError: i18n.t('branchGate.missingTenantId'),
              })
              return { ok: false, reason: 'missing_tenant_id' }
            }
            useManagerStore.getState().setActiveBranchContext({
              tenantId: selectedTenantId,
              tenantName: selectedBranch?.name ?? null,
              branchId: selectedBranch?.id ?? null,
            })
            setTenantSlugAttribute(selectedBranch?.tenant_slug ?? null)
            syncBranchUrlParam(selectedBranch?.id ?? null)
            const roleResult = await get().syncRoleForBranch(selectedBranch?.id ?? null)
            set({
              orgId,
              branches,
              branchBootstrapStatus: 'ready',
              branchError: roleResult.ok ? null : roleResult.error ?? null,
            })
            return { ok: true, branches }
          } catch (err) {
            Sentry.captureException(err, {
              tags: { module: 'auth', action: 'bootstrap_branches' },
              extra: { orgId },
            })
            set({
              orgId,
              branches: [],
              branchBootstrapStatus: 'error',
              branchError: formatApiError(err, i18n.t('common.requestFailed')),
            })
            return { ok: false, reason: 'error' }
          }
        })()

        bootstrapBranchesInFlight = { orgId, promise: requestPromise }
        try {
          return await requestPromise
        } finally {
          if (bootstrapBranchesInFlight?.promise === requestPromise) {
            bootstrapBranchesInFlight = null
          }
        }
      },
      ensureBranchBootstrap: async () => {
        const { token, branchBootstrapStatus, orgId, fetchProfile, bootstrapBranches } = get()
        if (!token) return { ok: false, reason: 'no_token' }
        if (branchBootstrapStatus === 'loading') return { ok: false, reason: 'loading' }
        if (branchBootstrapStatus === 'ready' || branchBootstrapStatus === 'no_branch') {
          return { ok: true, status: branchBootstrapStatus }
        }
        if (orgId) return bootstrapBranches(orgId)
        return fetchProfile()
      },
      syncRoleForBranch: async (branchId) => {
        if (!branchId) return { ok: false, reason: 'missing_branch_id' }
        try {
          const data = await getMyBranchRole(branchId)
          const resolvedRole = data?.branch_role ?? (data?.is_org_owner_admin ? 'admin' : null)
          if (!resolvedRole) return { ok: false, reason: 'missing_branch_role' }
          useManagerStore.getState().setBranchRole(resolvedRole)
          return { ok: true, role: normalizeRole(resolvedRole), data }
        } catch (err) {
          return {
            ok: false,
            reason: 'error',
            error: formatApiError(err, i18n.t('common.requestFailed')),
          }
        }
      },
      clearLocalSession: () => {
        fetchProfileInFlight = null
        bootstrapBranchesInFlight = null
        clearAuthToken()
        clearRefreshToken()
        syncBranchUrlParam(null)
        Sentry.setUser(null)
        // Reset GrowthBook identity so the next login on this device/tab
        // doesn't inherit the previous user's targeting attributes.
        growthbook.setAttributes({})
        queryClient.clear()
        set({
          token: null,
          user: null,
          orgId: null,
          branches: [],
          branchBootstrapStatus: 'idle',
          branchError: null,
        })
        useManagerStore.getState().clearManager()
        useNotificationStore.getState().reset()
      },
      fetchProfile: async () => {
        const token = getAuthToken()
        if (!token) return { ok: false }
        if (fetchProfileInFlight) return fetchProfileInFlight

        fetchProfileInFlight = (async () => {
          try {
            const { data } = await api.get('/manager/auth/me')
            const resolvedOrgId =
              data?.org_id ?? data?.orgId ?? data?.organization_id ?? null
            set((state) => ({
              user: {
                ...state.user,
                id: data.id ?? state.user?.id,
                name:
                  data.name ??
                  data.full_name ??
                  data.fullName ??
                  state.user?.name,
                username: data.username ?? state.user?.username,
                email: data.email ?? state.user?.email,
                phone: data.phone ?? state.user?.phone,
                avatar_url: data.avatar_url ?? null,
                companyName: data.company_name ?? state.user?.companyName,
                is_onboarding:
                  typeof data?.is_onboarding === 'boolean'
                    ? data.is_onboarding
                    : state.user?.is_onboarding,
              },
              orgId: resolvedOrgId ?? state.orgId,
            }))
            // Keep Sentry user identity up-to-date with the server profile.
            // This covers both the post-login enrichment and the page-reload
            // rehydration path (onRehydrateStorage → fetchProfile).
            const updatedUser = get().user
            Sentry.setUser({
              username: updatedUser?.email ?? null,
              email: updatedUser?.email ?? null,
              name: updatedUser?.name ?? null,
            })
            // GrowthBook targeting attributes: minimal PII (id + orgId only),
            // pushed here since fetchProfile runs after both login and
            // page-reload rehydration.
            if (updatedUser?.id) {
              growthbook.setAttributes({
                ...growthbook.getAttributes(),
                id: updatedUser.id,
                orgId: get().orgId ?? null,
              })
            }
            useManagerStore.getState().setProfileContext({
              role: null,
              tenantName: data.tenant_name ?? data.company_name ?? null,
            })
            await get().bootstrapBranches(resolvedOrgId)
            return { ok: true }
          } catch (err) {
            Sentry.captureException(err, {
              tags: { module: 'auth', action: 'fetch_profile' },
              extra: { userId: get().user?.email },
            })
            set({
              branchBootstrapStatus: 'error',
              branchError: i18n.t('common.requestFailed'),
            })
            return { ok: false }
          } finally {
            fetchProfileInFlight = null
          }
        })()

        return fetchProfileInFlight
      },
      logout: async () => {
        fetchProfileInFlight = null
        bootstrapBranchesInFlight = null
        await clearPushOnLogout(useManagerStore.getState().tenantId)
        const refresh = getRefreshToken()
        if (refresh) {
          try {
            await axios.post(
              `${CHATIZI_API_BASE_URL}/manager/auth/logout`,
              { refresh_token: refresh },
              { timeout: 15000, headers: { 'Content-Type': 'application/json' } }
            )
          } catch (err) {
            // Best-effort server-side logout; still clear local session
            Sentry.addBreadcrumb({
              message: 'Server-side logout token revocation failed',
              level: 'info',
              data: { error: err?.message },
            })
          }
        }
        get().clearLocalSession()
        try {
          if (auth) await signOut(auth)
        } catch (err) {
          // Best-effort Firebase sign-out; not worth surfacing to user
          Sentry.addBreadcrumb({
            message: 'Firebase sign-out failed during logout',
            level: 'info',
            data: { error: err?.message },
          })
        }
      },
      applyAuthResponse: async (data, emailFallback = '') => {
        const { access_token, refresh_token, user: userObj } = data
        if (refresh_token != null && refresh_token !== '') {
          setRefreshToken(refresh_token)
        }
        setAuthToken(access_token)
        // /manager/auth/login returns fields flat (user_id, email, name, org_id),
        // not nested under `user` — Google login's response may differ, so prefer
        // userObj.* when present and fall back to the flat fields.
        const resolvedId = userObj?.id ?? data.user_id ?? null
        const resolvedEmail = userObj?.email ?? data.email ?? emailFallback
        const resolvedName =
          userObj?.name ?? userObj?.full_name ?? userObj?.fullName ?? data.name ?? null
        set({
          token: access_token,
          user: {
            id: resolvedId,
            name: resolvedName,
            username: resolvedEmail,
            email: resolvedEmail,
            role: userObj?.role ? normalizeRole(userObj.role) : null,
            is_onboarding:
              typeof userObj?.is_onboarding === 'boolean'
                ? userObj.is_onboarding
                : null,
          },
        })
        // Tag the Sentry session immediately on login so Session Replay is
        // associated with the real user rather than "anonymous".
        Sentry.setUser({ username: resolvedEmail, email: resolvedEmail, name: resolvedName })
        await get().fetchProfile()
      },
      login: async (email, password) => {
        try {
          const res = await api.post('/manager/auth/login', {
            email: email.trim(),
            password,
          })
          await get().applyAuthResponse(res.data, email.trim())
          return { ok: true, tenant_id: res.data.tenant_id ?? null }
        } catch (err) {
          const status = err?.response?.status
          const detail = err?.response?.data?.detail
          if (
            status === 401 &&
            typeof detail === 'string' &&
            detail.trim().toLowerCase() === 'invalid credentials'
          ) {
            return { ok: false, error: i18n.t('authErrors.invalidCredentials') }
          }
          return { ok: false, error: formatApiError(err, i18n.t('common.requestFailed')) }
        }
      },
      // Body field `id_token` must match your backend; change here if the API expects another name.
      loginWithGoogle: async () => {
        if (!isFirebaseAuthConfigured() || !auth) {
          const hint = getFirebaseAuthConfigHint()
          return {
            ok: false,
            error: hint || i18n.t('authErrors.googleNotConfigured'),
          }
        }
        try {
          const credential = await signInWithPopup(auth, getGoogleProvider())
          const idToken = await credential.user.getIdToken()
          const res = await api.post(AUTH_GOOGLE_VERIFY_PATH, {
            id_token: idToken,
          })
          await get().applyAuthResponse(
            res.data,
            credential.user.email?.trim() ?? ''
          )
          return { ok: true, tenant_id: res.data.tenant_id ?? null }
        } catch (err) {
          if (typeof err?.code === 'string' && err.code.startsWith('auth/')) {
            if (err.code === 'auth/popup-closed-by-user') {
              return { ok: false, error: i18n.t('authErrors.signInCancelled') }
            }
            return {
              ok: false,
              error: err.message || i18n.t('authErrors.googleSignInFailed'),
            }
          }
          Sentry.captureException(err, {
            tags: { module: 'auth', action: 'login_with_google' },
          })
          return {
            ok: false,
            error: formatApiError(err, i18n.t('common.requestFailed')),
          }
        }
      },
    }),
    {
      name: 'growchat-auth',
      partialize: (s) => ({ token: s.token, user: s.user }),
      onRehydrateStorage: () => (state, err) => {
        if (!err && state?.token) {
          setAuthToken(state.token)
          queueMicrotask(() => {
            void useAuthStore.getState().fetchProfile()
          })
        }
        state?.setHasHydrated(true)
      },
    }
  )
)

// Listen for 401 logout signals dispatched by the API client.
// Using an event avoids a circular import (client.js → authStore.js → client.js).
window.addEventListener('auth:logout', () => {
  useAuthStore.getState().logout()
})

window.addEventListener('auth:subscription-expired', () => {
  useAuthStore.getState().clearLocalSession()
})

window.addEventListener('auth:token-refreshed', (e) => {
  const access = e.detail?.access_token
  if (access) useAuthStore.getState().setToken(access)
})
